import { h, JSX } from 'preact';
import ObservingComponent from '../componentBases/observingComponent';
import TableStatusDisplayProps from './tableStatusDisplayProps';
import Modal from '../containers/modal';
import Button from '../buttons/button';
import ButtonColors from '../buttons/buttonColors';
import Alert from '../alerts/alert';
import Field from '../fields/field';
import FormattedDate from '../text/formattedDate';
import { DateFormats, DateUtils, TableDisplayItem, TableDisplayMeetingItem, StringUtils } from 'core.frontend';

class TableStatusDisplayPage extends ObservingComponent<TableStatusDisplayProps> {

    private readonly gridColumnWidth = 100;
    private readonly gridColumnMinutes = 30;

    public componentWillMount(): void {
        this.registerUpdateObserver(this.props.page.observationProvider);
    }

    private getGridDurationMinutes = () => {
        return this.props.page.gridTimes.length * this.gridColumnMinutes;
    }

    private getVisibleMeetingStartMinutes = (meeting: TableDisplayMeetingItem) => {
        const minutesFromGridStart = DateUtils.getMinutesDifference(meeting.startDateTime, this.props.page.firstGridDateTime);

        return Math.max(0, minutesFromGridStart);
    }

    private getVisibleMeetingEndMinutes = (meeting: TableDisplayMeetingItem) => {
        const minutesFromGridStart = DateUtils.getMinutesDifference(meeting.endDateTime, this.props.page.firstGridDateTime);

        return Math.min(this.getGridDurationMinutes(), minutesFromGridStart);
    }

    private isMeetingVisibleOnGrid = (meeting: TableDisplayMeetingItem) => {
        return this.getVisibleMeetingEndMinutes(meeting) > this.getVisibleMeetingStartMinutes(meeting);
    }

    private getCurrentTimeLineLeft = () => {
        const minutesFromGridStart = DateUtils.getMinutesDifference(this.props.page.tableDisplayData.currentDateTimeOffset, this.props.page.firstGridDateTime);

        return minutesFromGridStart / this.gridColumnMinutes * this.gridColumnWidth;
    }

    private isCurrentTimeVisibleOnGrid = () => {
        const minutesFromGridStart = DateUtils.getMinutesDifference(this.props.page.tableDisplayData.currentDateTimeOffset, this.props.page.firstGridDateTime);

        return minutesFromGridStart >= 0 && minutesFromGridStart <= this.getGridDurationMinutes();
    }

    private getMeetingBlockWidth = (meeting: TableDisplayMeetingItem) => {
        const lengthInMinutes = this.getVisibleMeetingEndMinutes(meeting) - this.getVisibleMeetingStartMinutes(meeting);

        return lengthInMinutes / this.gridColumnMinutes * this.gridColumnWidth;
    }

    private getMeetingBlockLeft = (meeting: TableDisplayMeetingItem) => {
        const minutesFromGridStart = this.getVisibleMeetingStartMinutes(meeting);

        return minutesFromGridStart / this.gridColumnMinutes * this.gridColumnWidth;
    }

    private getTableName = (tableId: number): string => {
        const table = this.props.page.getTable(tableId);

        return table ? table.tableName : '';
    }

    private getTableSeatCount = (tableId: number): number => {
        const table = this.props.page.getTable(tableId);

        return table ? table.seatCount : 0;
    }

    private renderMeetingContent = (meeting: TableDisplayMeetingItem): JSX.Element => {
        const { page } = this.props;

        return (
            <span class="meeting-display-meeting-content">
                {page.form.model.displayMeetingName && meeting.meetingName &&
                    <span>
                        {StringUtils.truncate(meeting.meetingName, 30)}

                        {page.form.model.displayMeetingNumber &&
                            <span> ({meeting.meetingNumber})</span>
                        }
                    </span>
                }

                {page.form.model.displayMeetingNumber && !(page.form.model.displayMeetingName && meeting.meetingName) &&
                    <span>{meeting.meetingNumber}</span>
                }

                {page.form.model.displayMeetingTime &&
                    <span>
                        <FormattedDate
                            format={DateFormats.hhCmm}
                            date={meeting.startDateTime} />

                        <span> - </span>

                        <FormattedDate
                            format={DateFormats.hhCmm}
                            date={meeting.endDateTime} />
                    </span>
                }

                {page.form.model.displayMeetingPoc && meeting.pocName &&
                    <span>POC: {meeting.pocName}</span>
                }

                {page.form.model.displayAttendeesSeated && meeting.attendeesSeated &&
                    <span>Seated</span>
                }
            </span>
        );
    }

    private renderTableLabel = (table: TableDisplayItem): JSX.Element => {
        const { page } = this.props;

        return (
            <div>
                <b>{table.tableName}</b>

                {page.form.model.displaySeatCount &&
                    <div class="text-sm">{table.seatCount} seats</div>
                }
            </div>
        );
    }

    private renderMeetingListHeaders = (): JSX.Element[] => {
        const { page } = this.props;
        const headers = [];

        if (page.form.model.displayMeetingName) {
            headers.push(<th>Meeting Name</th>);
        }

        if (page.form.model.displayMeetingNumber) {
            headers.push(<th class="fit-content">Meeting #</th>);
        }

        if (page.form.model.displayMeetingTime) {
            headers.push(<th class="fit-content">Time</th>);
        }

        headers.push(<th class="text-center">Table</th>);

        if (page.form.model.displaySeatCount) {
            headers.push(<th class="text-center fit-content">Seats</th>);
        }

        if (page.form.model.displayMeetingPoc) {
            headers.push(<th class="text-center">POC</th>);
        }

        if (page.form.model.displayAttendeesSeated) {
            headers.push(<th class="text-center fit-content">Seated</th>);
        }

        return headers;
    }

    private renderMeetingListCells = (meeting: TableDisplayMeetingItem): JSX.Element[] => {
        const { page } = this.props;
        const cells = [];

        if (page.form.model.displayMeetingName) {
            cells.push(<td>{meeting.meetingName}</td>);
        }

        if (page.form.model.displayMeetingNumber) {
            cells.push(<td class="text-center fit-content">{meeting.meetingNumber}</td>);
        }

        if (page.form.model.displayMeetingTime) {
            cells.push(
                <td class="text-center fit-content">
                    <FormattedDate
                        format={DateFormats.hhCmm}
                        date={meeting.startDateTime} />

                    <span> - </span>

                    <FormattedDate
                        format={DateFormats.hhCmm}
                        date={meeting.endDateTime} />
                </td>
            );
        }

        cells.push(<td class="text-center">{this.getTableName(meeting.tableId)}</td>);

        if (page.form.model.displaySeatCount) {
            cells.push(<td class="text-center fit-content">{this.getTableSeatCount(meeting.tableId)}</td>);
        }

        if (page.form.model.displayMeetingPoc) {
            cells.push(<td class="text-center">{meeting.pocName}</td>);
        }

        if (page.form.model.displayAttendeesSeated) {
            cells.push(<td class="text-center fit-content">{meeting.attendeesSeated ? 'Yes' : 'No'}</td>);
        }

        return cells;
    }

    public render({ page }: TableStatusDisplayProps): JSX.Element {
        return (
            <div class="display-page-wrapper">
                <Alert
                    className="mb-0"
                    alert={page.alert} />

                {page.settingsVisible &&
                    <Modal
                        heading="Table Status Display Settings"
                        close={() => {}}
                        hideBackButton={true}
                        footerRightButtons={[
                            <Button
                                color={ButtonColors.Primary}
                                button={page.submitButton} />
                        ]}>

                        <div class="form-container room-status-display-settings">
                            <p>
                                <b>Note: </b> Only finalized meetings assigned to a table will be displayed. Refresh the page to access these settings again.
                            </p>

                            <h4 class="section-header">Display Options</h4>

                            <div class="room-status-settings-row">
                                <b>Views:</b>

                                <Field
                                    className="room-status-option-checkbox"
                                    field={page.form.fields[0]} />

                                <Field
                                    className="room-status-option-checkbox"
                                    field={page.form.fields[1]} />
                            </div>

                            <div class="room-status-settings-row">
                                <b>Content:</b>

                                <Field
                                    className="room-status-content-checkbox"
                                    field={page.form.fields[2]} />

                                <Field
                                    className="room-status-content-checkbox"
                                    field={page.form.fields[3]} />

                                <Field
                                    className="room-status-content-checkbox"
                                    field={page.form.fields[4]} />

                                <Field
                                    className="room-status-content-checkbox"
                                    field={page.form.fields[5]} />

                                <Field
                                    className="room-status-content-checkbox"
                                    field={page.form.fields[6]} />

                                <Field
                                    className="room-status-content-checkbox"
                                    field={page.form.fields[7]} />
                            </div>

                            <div class="room-status-venue-header">
                                <b>Tables</b>
                                <button
                                    type="button"
                                    onClick={page.toggleAllTables}>Toggle All</button>
                            </div>

                            <div class="room-status-venue-options">
                                {page.tableFields.map((field) =>
                                    <Field
                                        className="room-status-option-checkbox"
                                        field={field} />
                                )}
                            </div>
                        </div>
                    </Modal>
                }

                {!page.settingsVisible &&
                    <div class="meeting-display horizontal">
                        <div class="meeting-display-header">
                            <div>{page.tableDisplayData.eventName}</div>

                            <FormattedDate
                                className="meeting-display-date"
                                date={page.tableDisplayData.currentDateTimeOffset}
                                format={DateFormats.MMMdYYYY} />

                            <FormattedDate
                                className="meeting-display-time"
                                date={page.tableDisplayData.currentDateTimeOffset}
                                format={DateFormats.hhCmm}/>
                        </div>
                        <div class="meeting-display-content">

                            {page.form.model.displayMeetingList &&
                                <div class={'meeting-display-list ' + (!page.form.model.displayMeetingGrid ? 'full' : '')}>
                                    <table>
                                        <thead>
                                            {this.renderMeetingListHeaders()}
                                        </thead>
                                        <tbody>
                                            {page.selectedMeetings.map((meeting) =>
                                                <tr>
                                                    {this.renderMeetingListCells(meeting)}
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            }

                            {page.form.model.displayMeetingGrid &&
                                <div class="meeting-display-grid">
                                    <table>
                                        <thead>
                                            <th></th>
                                            {page.gridTimes.map((time) =>
                                                <th class="meeting-display-grid-time">
                                                    <b>{time}</b>
                                                </th>
                                            )}
                                        </thead>
                                        <tbody>
                                            {page.selectedTables.map((table) =>
                                                <tr>
                                                    <td>
                                                        {this.renderTableLabel(table)}
                                                    </td>

                                                    <td
                                                        class="meeting-container-cell"
                                                        colSpan={100}>

                                                        {this.isCurrentTimeVisibleOnGrid() &&
                                                            <span
                                                                class="current-time-line"
                                                                style={{
                                                                    'left': this.getCurrentTimeLineLeft() + 'px'
                                                                }}>
                                                            </span>
                                                        }

                                                        {page.selectedMeetings.map((meeting) => {

                                                            if (meeting.tableId !== table.tableId) {
                                                                return;
                                                            }

                                                            if (!this.isMeetingVisibleOnGrid(meeting)) {
                                                                return;
                                                            }

                                                            return (
                                                                <span
                                                                    class="meeting-block"
                                                                    style={{
                                                                        'left': this.getMeetingBlockLeft(meeting) + 'px',
                                                                        'width': this.getMeetingBlockWidth(meeting) + 'px'
                                                                    }}>

                                                                    {this.renderMeetingContent(meeting)}
                                                                </span>
                                                            );
                                                        })}
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            }
                        </div>
                    </div>
                }
            </div>
        );
    }
}

export default TableStatusDisplayPage;
